// GL02 주문수집 순수 변환 로직 — request 비의존 모듈.
// 테스트에서 request 실모듈을 끌고 오면 CI Node 에서 깨진다 — 경계에서 끊는 레포 선례(monitoring/mapping.ts) 준수.
import type { Dayjs } from 'dayjs'

export interface OrderCollectParams {
  /** 조회 시퀀스 — 동일 조건 재조회용 queryKey 구분자 (서버 미전달) */
  _seq?: number
  shipperCode?: string
  /** 마감일자 범위 (yyyyMMdd) */
  clsDateFrom?: string
  clsDateTo?: string
}

/** BE `GlobalOrderCollectionRowDto` 중 GL02_ITEM(prod MOMS_GRID_COLUMN_INFO 실측) 노출 대상 필드만 선별 */
export interface OrderCollectionItem {
  id: number
  status?: string | null
  deliveryItemId?: number | null
  countryType?: string | null
  orderId?: string | null
  orderOptionId?: number | null
  deliveryState?: string | null
  processMemo?: string | null
  memo?: string | null
  jsonId?: string | null
  orderDate?: string | null
  payDate?: string | null
  companyType?: string | null
  orderState?: string | null
  goodsOption?: string | null
  quantity?: number | null
  orderOptionMemo?: string | null
  orderOptionState?: string | null
  goodsNo?: number | null
  goodsType?: string | null
  styleNo?: string | null
  companyId?: string | null
  productKindType?: string | null
  imageUrl?: string | null
  brand?: string | null
  goodsLocation?: string | null
  languageKoGoodsName?: string | null
  languageKoOptionName?: string | null
  logisticsNo?: number | null
  memberUserName?: string | null
  memberId?: number | null
  memberEmail?: string | null
  packageInvoice?: string | null
  globalInvoiceNo?: string | null
  globalCustomerUserName?: string | null
  globalCustomerZipcode?: string | null
  globalCustomerState?: string | null
  globalCustomerCity?: string | null
  globalCustomerAddress1?: string | null
  globalCustomerAddress2?: string | null
}

export type OrderCollectRow = OrderCollectionItem & { _rowKey: string }

/** dayjs 값 또는 문자열 → yyyyMMdd (BE 조회 API 계약, LLD-9610 §3.1) */
export const asCompactDate = (v: unknown): string | undefined => {
  if (v && typeof v === 'object' && 'format' in (v as object)) return (v as { format: (f: string) => string }).format('YYYYMMDD')
  return typeof v === 'string' && v ? v.replace(/-/g, '') : undefined
}

// FilterArea 폼 값(필드 id 키) → 2.0 조회 API 파라미터.
export const toSearchParams = (values: Record<string, unknown>): OrderCollectParams => {
  const range = values['f-clsDate'] as unknown[] | undefined
  return {
    shipperCode: (values['f-shipper'] as string) || undefined,
    clsDateFrom: Array.isArray(range) ? asCompactDate(range[0]) : undefined,
    clsDateTo: Array.isArray(range) ? asCompactDate(range[1]) : undefined,
  }
}

export const toOrderCollectRow = (item: OrderCollectionItem, index: number): OrderCollectRow => ({
  ...item,
  _rowKey: String(item.id ?? index),
})

// ── 조회 조건 세션 복원 ──────────────────────────────────────────
// URL ⇄ 조회조건 변환은 `../searchQuery.ts` 가 담당한다(결정 12 — 레포 searchQuery.ts 관행).
// 이 모듈에는 폼 값 변환만 남긴다.

/** yyyyMMdd(8자리) → yyyy-MM-dd. 레포에 dayjs customParseFormat 플러그인이 없어(stockMoveHistory/searchQuery.ts 선례)
 *  dayjs(yyyyMMdd, 'YYYYMMDD') 대신 ISO 형태로 바꿔 넘겨야 한다. */
export const compactDateToIso = (yyyymmdd: string): string => `${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6)}`

/** 복원 조회조건 → FilterArea 폼 값(필드 id 키). clsDateFrom/To(yyyyMMdd) → dayjs 변환은 toDate 가 담당(입력은 yyyy-MM-dd ISO) */
export const toFilterFormValues = (params: OrderCollectParams, toDate: (isoDate: string) => unknown = (d) => d): Record<string, unknown> => {
  const values: Record<string, unknown> = {}
  if (params.shipperCode) values['f-shipper'] = params.shipperCode
  if (params.clsDateFrom && params.clsDateTo) values['f-clsDate'] = [toDate(compactDateToIso(params.clsDateFrom)), toDate(compactDateToIso(params.clsDateTo))]
  return values
}

/** 액션바 목록 검색 — 행 전체 필드 부분 일치(대소문자 무시), 빈 검색어는 전체 통과 */
export const matchesQuery = (row: Record<string, unknown>, q: string): boolean =>
  !q || Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(q.toLowerCase()))

// ── 수집 파라미터 폼 ──────────────────────────────────────────────
// dev DB MOMS_GRID_COLUMN_INFO(MENU_CD='GL02', GRID_CD='GL02_HEAD') 실측 필드(12개 중 page 제외 — 2.0 은 서버가 내부 페이지 순회).

export interface CollectFormValues {
  /** 수집기간 [시작, 종료] — pbo-core FormGroup 'GL02 주문수집' 등록폼 소확장(2026-07-23)에 맞춘 단일 range 필드 */
  collectPeriod?: [Dayjs, Dayjs]
  orderNos?: string
  deliveryCountry?: string
  styleNos?: string
  goodsIds?: string
  goodsName?: string
  hblNumber?: string
  invoiceNumber?: string
  limit?: number
  lastDeliveryItemId?: number
}

export interface CollectGlobalOrderParams {
  searchGapStartDate?: string
  searchGapEndDate?: string
  orderNos?: string
  deliveryCountry?: string
  styleNos?: string
  goodsIds?: string
  goodsName?: string
  hblNumber?: string
  invoiceNumber?: string
  limit?: number
  lastDeliveryItemId?: number
}

const asIsoDate = (v: unknown): string | undefined => {
  if (v && typeof v === 'object' && 'format' in (v as object)) return (v as { format: (f: string) => string }).format('YYYY-MM-DD')
  return typeof v === 'string' && v ? v : undefined
}

const asTrimmed = (v?: string): string | undefined => (v && v.trim() ? v.trim() : undefined)

/** 수집 폼 값 → BE `POST /collect` 요청 바디 (LLD-9610 §3.2, 전 필드 optional) */
export const toCollectParams = (values: CollectFormValues): CollectGlobalOrderParams => {
  const [start, end] = values.collectPeriod ?? [undefined, undefined]
  return {
    searchGapStartDate: asIsoDate(start),
    searchGapEndDate: asIsoDate(end),
    orderNos: asTrimmed(values.orderNos),
    deliveryCountry: asTrimmed(values.deliveryCountry),
    styleNos: asTrimmed(values.styleNos),
    goodsIds: asTrimmed(values.goodsIds),
    goodsName: asTrimmed(values.goodsName),
    hblNumber: asTrimmed(values.hblNumber),
    invoiceNumber: asTrimmed(values.invoiceNumber),
    limit: values.limit,
    lastDeliveryItemId: values.lastDeliveryItemId,
  }
}

// 수집 폼 세션 복원 — 페이지 전용 sessionStorage 키에 직접 저장한다(MenuStorage.menu 의 get/set/try-catch 스타일 준용).
// MenuStorage.menuOnSession.setTabSearch 는 검토했으나 탭 엔트리의 공용 .search 필드를 매번 덮어써
// 조회 필터(setSearch)와 같은 화면에서 같이 쓰면 마지막 호출이 다른 쪽 값을 지운다(실측 확인) — 그래서 별도 키로 뺀다.
// 조회 필터와 달리 URL 에는 싣지 않는다 — 주문번호·상품명 등 입력값을 주소창/히스토리에 남기지 않기 위함.
// 저장 payload 는 toCollectParams 출력(CollectGlobalOrderParams)을 그대로 쓴다 — 이미 트림·ISO 변환이 끝난 값이라 왕복 변환이 가장 단순하다.
export const COLLECT_FORM_SESSION_KEY = 'moms_gl14_collect_form'

const asStrOrUndef = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)
const asNumOrUndef = (v: unknown): number | undefined => {
  if (typeof v === 'number') return Number.isNaN(v) ? undefined : v
  if (typeof v !== 'string' || v === '') return undefined
  const n = Number(v)
  return Number.isNaN(n) ? undefined : n
}

/** 저장된 수집 폼 값(JSON.parse 결과) → CollectFormValues. searchGapStartDate/EndDate → collectPeriod 는 toDate 가 담당 */
export const fromCollectParams = (stored: Record<string, unknown>, toDate: (isoDate: string) => Dayjs): CollectFormValues => {
  const start = asStrOrUndef(stored.searchGapStartDate)
  const end = asStrOrUndef(stored.searchGapEndDate)
  return {
    collectPeriod: start && end ? [toDate(start), toDate(end)] : undefined,
    orderNos: asStrOrUndef(stored.orderNos),
    deliveryCountry: asStrOrUndef(stored.deliveryCountry),
    styleNos: asStrOrUndef(stored.styleNos),
    goodsIds: asStrOrUndef(stored.goodsIds),
    goodsName: asStrOrUndef(stored.goodsName),
    hblNumber: asStrOrUndef(stored.hblNumber),
    invoiceNumber: asStrOrUndef(stored.invoiceNumber),
    limit: asNumOrUndef(stored.limit),
    lastDeliveryItemId: asNumOrUndef(stored.lastDeliveryItemId),
  }
}

export interface CollectGlobalOrderResult {
  collectedCount: number
  skuMappingCount: number
  pageCount: number
  postProcessed: boolean
  message?: string | null
}

/** 수집 성공 메시지 — BE message 우선(수집 0건 "수집 대상이 없습니다." 등), 없으면 건수 요약 */
export const toCollectSummary = (r: CollectGlobalOrderResult): string =>
  r.message || `수집 ${r.collectedCount}건, SKU 매핑 ${r.skuMappingCount}건 완료`

/** BE meta.message 우선, 빈 body 응답은 HTTP 상태를 함께 노출 */
export const apiErrMessage = (e: unknown): string => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message ?? (resp?.status ? `처리에 실패했습니다. (HTTP ${resp.status})` : '처리에 실패했습니다.')
}
